export function hexToRgb(hex: string) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [255, 255, 255]
}
export function rgb(value: [number, number, number]) {
  return `rgb(${value.join(',')})`
}
export function rgba(value: [number, number, number], a = 1, base = 'inherit') {
  if (base === 'inherit') {
    return `rgba(${[...value, a].join(',')})`
  }
  const baseRGB = hexToRgb(base)
  const [rr, gg, bb] = value.map((c, i) => Math.floor(c * a + baseRGB[i] * (1 - a)))
  return rgb([rr, gg, bb])
}

export const baseColors: { [name: string]: [number, number, number] } = {
  cta: [2, 187, 79],
  primaryAccent: [41, 139, 255],
  warning: [238, 169, 0],
  danger: [255, 102, 102],

  dark: [64, 78, 89],
  medium: [122, 145, 168],
  light: [153, 173, 193],
  faded: [247, 249, 251],
  disabledLight: [204, 204, 204],
  disabledDark: [153, 153, 153],
  label: [102, 102, 102],
  white: [255, 255, 255],

  brand: [255, 0, 153],
}

const positiveFocusColor = rgba([2, 187, 79], 0.5, '#ffffff')
const positiveHoverColor = rgb([5, 158, 69])
const positiveActiveColor = rgb([3, 120, 52])
const positiveBorderColor = rgba([3, 120, 52], 0.5)

export const text = {
  normal: rgb(baseColors.dark),
  normalHover: rgba(baseColors.dark, 0.6),
  normalActive: rgba(baseColors.dark, 0.8),
  dimmed: rgba(baseColors.dark, 0.5),
  light: rgb(baseColors.white),
  lightHover: rgba(baseColors.white, 0.8),
  lightActive: rgb(baseColors.white),
}

export const accents = {
  default: text.normal,
  defaultFocus: text.normal,
  defaultHover: text.normal,
  defaultActive: text.normal,
  defaultBorder: text.normal,
  cta: rgb(baseColors.cta),
  ctaFocus: positiveFocusColor,
  ctaHover: positiveHoverColor,
  ctaActive: positiveActiveColor,
  ctaBorder: positiveBorderColor,
  primary: rgb(baseColors.primaryAccent),
  primaryFocus: rgba([41, 139, 255], 0.5, '#ffffff'),
  primaryHover: rgb([29, 112, 210]),
  primaryActive: rgb([10, 87, 178]),
  primaryBorder: rgba([10, 87, 178], 0.5),
  secondary: rgb(baseColors.medium),
  secondaryFocus: rgba([122, 145, 168], 0.5, '#ffffff'),
  secondaryHover: rgb([86, 112, 139]),
  secondaryActive: rgb([61, 87, 114]),
  secondaryBorder: rgba([61, 87, 114], 0.5),
  secondaryLight: rgb([248, 249, 250]),
  secondaryLightFocus: rgba([122, 145, 168], 0.25, '#ffffff'),
  secondaryLightHover: rgb([227, 229, 232]),
  secondaryLightActive: rgb([193, 195, 199]),
  secondaryLightBorder: rgba([61, 87, 114], 0.5),
  positive: rgb(baseColors.cta),
  positiveFocus: positiveFocusColor,
  positiveHover: positiveHoverColor,
  positiveActive: positiveActiveColor,
  positiveBorder: positiveBorderColor,
  negative: rgb(baseColors.danger),
  negativeFocus: rgba([255, 102, 102], 0.5, '#ffffff'),
  negativeHover: rgb([226, 93, 93]),
  negativeActive: rgb([203, 74, 74]),
  negativeBorder: rgba([203, 74, 74], 0.5),
  alert: rgb(baseColors.warning),
  alertFocus: rgba([238, 169, 0], 0.5, '#ffffff'),
  alertHover: rgb([221, 144, 0]),
  alertActive: rgb([204, 144, 0]),
  alertBorder: rgba([204, 144, 0], 0.5),
  brand: rgb(baseColors.brand),
  brandFocus: rgba([255, 0, 153], 0.25, '#ffffff'),
  brandHover: rgb([249, 0, 148]),
  brandActive: rgb([244, 0, 144]),
  brandBorder: rgba([244, 0, 144], 0.5),
  white: rgb(baseColors.white),
  whiteFocus: rgba([255, 255, 255], 0.5, '#ffffff'),
  whiteHover: rgb(baseColors.white),
  whiteActive: rgb(baseColors.white),
  whiteActiveBorder: rgba(baseColors.white, 0.5),
}

export const backgrounds = {
  main: 'white',
  mainHover: rgb([252, 252, 252]),
  mainFocus: rgba([0, 0, 0], 0.03),
  gray: rgb([232, 233, 235]),
  grayHover: rgb([232, 233, 235]),
  grayFocus: rgb([232, 233, 235]),
  content: rgb([247, 249, 251]),
  contentHover: rgb([247, 249, 251]),
  cta: rgba(baseColors.cta, 0.05),
  primary: rgba(baseColors.primaryAccent, 0.05),
  secondary: rgba([248, 249, 250], 0.05),
  negative: rgba(baseColors.danger, 0.05),
  positive: rgba(baseColors.cta, 0.05),
  alert: rgba(baseColors.warning, 0.1),
  brand: rgba(baseColors.brand, 0.05),
  overlay: rgba([44, 55, 61], 0.2),
  overlayLight: rgba([255, 255, 255], 0.5),
  dark: rgb(baseColors.dark),
}

export const borders = {
  regular: rgba([64, 78, 89], 0.1),
  primary: rgba([41, 139, 255], 0.5),
  dim: rgba([226, 226, 226], 0.5),
}

export const ui = {
  dark: rgb(baseColors.dark),
  medium: rgb(baseColors.medium),
  light: rgb(baseColors.light),
  faded: rgb(baseColors.faded),
  disabledLight: rgb(baseColors.disabledLight),
  disabledDark: rgb(baseColors.disabledDark),
  label: rgb(baseColors.label),
  white: rgb(baseColors.white),
}
